/**
 * سرویس احراز هویت تردد (Passkey / PWA Push)
 */

export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let charCode of bytes) {
    str += String.fromCharCode(charCode);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    buffer[i] = rawData.charCodeAt(i);
  }
  return buffer.buffer;
}

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * گام اول و دوم Passkey:
 * ۱. ارسال موبایل به بک‌اند و دریافت Challenge + Credential ID
 * ۲. باز کردن سنسور بیومتریک و ساخت امضای سخت‌افزاری
 */
export async function getPasskeyAssertion(axiosInstance, mobile) {
  if (!window.PublicKeyCredential) {
    throw new Error('دستگاه یا مرورگر شما از Passkey / سنسور بیومتریک پشتیبانی نمی‌کند.');
  }

  if (!mobile) {
    throw new Error('شماره موبایل پرسنل جهت استعلام کلید امنیتی ارسال نشده است.');
  }

  // ۱. مرحله اول: دادن شماره موبایل به سرور و گرفتن Challenge
  const { data: serverOptions } = await axiosInstance.post('/api/webauthn/login/options', {
    mobile: mobile
  });

  // اگر بک‌اند گزینه‌ها را در ساختار استاندارد یا تودرتو فرستاده باشد:
  const challenge = serverOptions.challenge;
  const allowCredentials = (serverOptions.allowCredentials || []).map(cred => ({
    type: 'public-key',
    id: typeof cred.id === 'string' ? base64urlToBuffer(cred.id) : cred.id
  }));

  // ۲. مرحله دوم: فراخوانی چیپست سخت‌افزاری گوشی (اثرانگشت / FaceID)
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: typeof challenge === 'string' ? base64urlToBuffer(challenge) : challenge,
      allowCredentials: allowCredentials,
      userVerification: 'required',
      timeout: 60000
    }
  });

  // آماده‌سازی خروجی استاندارد
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64url(credential.response.authenticatorData),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
    }
  };
}

/**
 * دریافت توکن سخت‌افزاری PWA Push
 */
export async function getPwaPushData(vapidPublicKey) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('مرورگر از قابلیت سخت‌افزاری Push پشتیبانی نمی‌کند.');
  }

  const reg = await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();

  if (!subscription && vapidPublicKey) {
    const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedKey
    });
  }

  if (!subscription) {
    throw new Error('توکن نوتیفیکیشن PWA روی این دستگاه تنظیم نشده است.');
  }

  const rawKey = subscription.getKey('p256dh');
  const rawAuth = subscription.getKey('auth');

  return {
    endpoint: subscription.endpoint,
    p256dh: rawKey ? window.btoa(String.fromCharCode.apply(null, new Uint8Array(rawKey))) : null,
    auth: rawAuth ? window.btoa(String.fromCharCode.apply(null, new Uint8Array(rawAuth))) : null
  };
}

/**
 * متد اصلی ثبت تردد
 */
export async function submitAttendanceRecord(axiosInstance, params) {
  const {
    qrCode,
    action, // 'check_in' یا 'check_out'
    lat,
    lng,
    shiftId,
    mobile, // شماره موبایل پرسنل
    image = null, 
    nationalCode = null,
    verificationMethod, // 'passkey' یا 'pwa_push'
    vapidPublicKey = null
  } = params;

  let passkeyAssertion = null;
  let pwaPushData = null;

  // اگر روش passkey باشد، ابتدا موبایل به سرور می‌رود تا چالش صادر شود
  if (verificationMethod === 'passkey') {
    passkeyAssertion = await getPasskeyAssertion(axiosInstance, mobile);
  } else if (verificationMethod === 'pwa_push') {
    pwaPushData = await getPwaPushData(vapidPublicKey);
  }

  // پی‌لود نهایی جهت ثبت تردد
  const payload = {
    qr_code: qrCode,
    action: action,
    lat: lat,
    lng: lng,
    shift_id: shiftId,
    mobile: mobile,
    image: image, 
    national_code: nationalCode,
    verification_method: verificationMethod,
    passkey_assertion: passkeyAssertion,
    pwa_push_data: pwaPushData
  };

  return await axiosInstance.post('/api/attendance/record', payload);
}
